import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, X } from "lucide-react";
import api from "../lib/api";
import { useContent } from "../lib/content";
import { LANES } from "../data/lanes";
import Header from "../components/Header";

const VP = { x: 50, y: 3 };
const BX = [-40, -2, 30, 70, 102, 140];
const laneGeo = LANES.map((_, i) => {
  const cx = (BX[i] + BX[i + 1]) / 2;
  return { lx: VP.x + (cx - VP.x) * 0.5, ly: VP.y + (100 - VP.y) * 0.5 };
});

const ASSETS = "https://customer-assets-jai6qajn.emergentagent.net/job_design-lanes/artifacts";
const USER_PHOTOS = [
  { src: `${ASSETS}/w450ehis_ARAE.png`, title: "Antonio Eccleston — Artist Website" },
  { src: `${ASSETS}/6due3343_CC4.png`, title: "Craftsman Club №4 — Fraternity Website" },
  { src: `${ASSETS}/gwl2m6ga_EMU.png`, title: "EMU Huron Relays — Event Website" },
  { src: `${ASSETS}/5xp8xeii_Figma%20%231.png`, title: "Figma — Mobile Design Exploration" },
  { src: `${ASSETS}/chuvkf1j_Figma%20%232.png`, title: "Figma — App Wireframe Flow" },
  { src: `${ASSETS}/5ks2tnsf_Framer%20%231.png`, title: "GigPilot — Framer Build" },
  { src: `${ASSETS}/irvtvhzv_Leila.png`, title: "Leila Hayes Crochet Creations — Brand Website" },
  { src: `${ASSETS}/tc27p8r6_MABTFC.png`, title: "MABTFC — Coaches Association Website" },
  { src: `${ASSETS}/ovrjxxzz_Screenshot%202026-08-06%20164743.png`, title: "Figma — Website Design Canvas" },
  { src: `${ASSETS}/yx445m0l_SPED%20%231.png`, title: "SpEdFlo — Role Selection" },
  { src: `${ASSETS}/i7g951wh_SPED%20%232.png`, title: "SpEdFlo — Staff Awareness" },
  { src: `${ASSETS}/14meln9i_SPED%20%233.png`, title: "SpEdFlo — Emergency Mode" },
  { src: `${ASSETS}/jdbnvik2_SPED%20%234.png`, title: "SpEdFlo — Daily Schedule" },
  { src: `${ASSETS}/1q0xnohb_magic_edit%23TUFIUmN3R2pNcnMjMSNmZmM0ZmY3ZWIxM2NiZDE5ZTI4MWYxNTA1Yzk3NmRhNSMxMzc2IyNUUkFOU0ZPUk1BVElPTl9SRVFVRVNU.png`, title: "L.A.B. Track Club — Multi-Device" },
  { src: `${ASSETS}/ppk0yt1b_magic_edit%23TUFIUnRBU0dlNTQjMSNiODBmODU0ZWI1YzljMjNiZmVhNzZhYmQ0MTVkZmZiMyMxMzc2IyNUUkFOU0ZPUk1BVElPTl9SRVFVRVNU.png`, title: "LAB Connect — Mobile App" },
];

export default function Home() {
  const navigate = useNavigate();
  const content = useContent();
  const lanes = LANES.map((l, i) => ({ ...l, ...(content.lanes?.[i] || {}) }));
  const [hover, setHover] = useState(null);
  const [going, setGoing] = useState(null);
  const [imgs, setImgs] = useState(USER_PHOTOS);
  const [lightbox, setLightbox] = useState(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const active = hover ?? 0;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = () => setIsDesktop(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  // On tablet/mobile the lane stage is hidden — aim the spotlight straight down (lane 3).
  const beamIdx = isDesktop ? active : 2;

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  useEffect(() => {
    Promise.allSettled([api.get("/gallery"), api.get("/projects"), api.get("/case-studies")]).then(([g, p, c]) => {
      const gal = g.status === "fulfilled" && g.value.data.length
        ? g.value.data.map((x) => ({ src: x.src, title: x.title, thumb: x.thumb }))
        : USER_PHOTOS;
      const proj = p.status === "fulfilled" ? p.value.data.map((x) => ({ src: x.image, title: x.title })) : [];
      const cases = c.status === "fulfilled" ? c.value.data.map((x) => ({ src: x.hero_image, title: x.title })) : [];
      const seen = new Set();
      const list = [...gal, ...proj, ...cases].filter((x) => {
        if (!x.src || x.src.includes("images.unsplash.com") || seen.has(x.src)) return false;
        seen.add(x.src);
        return true;
      });
      setImgs(list.length ? list : USER_PHOTOS);
    });
  }, []);

  const reduce = useReducedMotion();
  const location = useLocation();
  const fromInterior = location.state?.fromInterior;

  const go = (lane) => {
    setGoing(lane.key);
    // Directional cue: top lanes (index<2) arrive from below, bottom lanes (index>2) arrive from above.
    // Passed to destination via location state; -1..+1 range.
    const idx = LANES.findIndex(l => l.key === lane.key);
    const laneDirection = (idx - 2) / 2;
    setTimeout(() => navigate(lane.route, { state: { laneDirection } }), reduce ? 220 : 520);
  };

  // Preload the first two hero images of a lane on hover so the destination never flashes.
  const preloadedRef = useState(() => new Set())[0];
  const preloadLane = (laneKey) => {
    if (preloadedRef.has(laneKey)) return;
    preloadedRef.add(laneKey);
    if (laneKey === "ux") {
      api.get("/case-studies").then((r) => (r.data || []).slice(0, 3).forEach((c) => { if (c.hero_image) new Image().src = c.hero_image; })).catch(() => {});
    } else {
      api.get(`/projects?lane=${laneKey}`).then((r) => (r.data || []).slice(0, 3).forEach((p) => { if (p.image) new Image().src = p.image; })).catch(() => {});
    }
  };

  const activeLane = lanes[active];

  return (
    <div className="grain relative h-screen w-screen overflow-hidden bg-void text-white flex flex-col" style={{ height: "100dvh" }}>
      <Header dark />

      {/* Reverse-portal reveal when returning home — pure opacity fade of the white
          overlay (GPU-cheap, never drops frames). The scene beneath plays its intro. */}
      {fromInterior && (
        <motion.div className="fixed inset-0 z-[80] bg-white pointer-events-none"
          style={{ willChange: "opacity", backfaceVisibility: "hidden" }}
          initial={{ opacity: 1 }} animate={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.22 : 0.55, ease: [0.65, 0, 0.35, 1] }} />
      )}

      {/* Faint spotlight beam — from the centered logo down to the highlighted lane */}
      <svg className="pointer-events-none fixed inset-0 w-full h-full z-[1]" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="beamFull" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.10" />
            <stop offset="42%" stopColor="#fff" stopOpacity="0.045" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <filter id="beamSoft" x="-30%" y="-10%" width="160%" height="120%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>
        <AnimatePresence>
          {!going && (
            <motion.polygon key={beamIdx}
              points={`${VP.x},6.2 ${BX[beamIdx]},100 ${BX[beamIdx + 1]},100`}
              fill="url(#beamFull)" filter="url(#beamSoft)"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} />
          )}
        </AnimatePresence>
      </svg>

      {/* SCENE — glides forward into the light on GO. GPU-only transform + opacity
          (no filter/blur animation, no layout properties). */}
      <motion.div
        className="relative z-10 flex-1 flex flex-col min-h-0"
        style={{ transformOrigin: "50% 40%", willChange: "transform, opacity", backfaceVisibility: "hidden" }}
        initial={false}
        animate={going
          ? (reduce ? { opacity: 0 } : { scale: 2.6, opacity: 0.35 })
          : { scale: 1, opacity: 1 }}
        transition={{ duration: reduce ? 0.22 : 0.55, ease: [0.65, 0, 0.35, 1] }}>

      {/* HERO */}
      <div className="relative z-20 px-6 md:px-10 pt-20 md:pt-24 [@media(max-height:768px)]:md:pt-20 shrink-0">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 0.5, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-white/50 mb-4">
          {content.hero_kicker}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1 initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-[clamp(2.5rem,8vw,7rem)] tracking-tighter leading-[0.9]">
            {content.hero_name}
          </motion.h1>
        </div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-5 text-lg md:text-xl text-white/80 font-light">{content.hero_role}</motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.55 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-3 max-w-md text-sm md:text-base text-white/55 leading-relaxed">{content.hero_tagline}</motion.p>

        {/* Active lane title + description — lives in document flow so it can never overlap the lanes */}
        <div className="hidden lg:block [@media(max-height:768px)]:lg:hidden mt-6 min-h-[72px] max-w-md">
          <AnimatePresence mode="wait">
            <motion.div key={activeLane.key}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
              <p className="font-mono text-[10px] tracking-[0.26em] uppercase text-white/45 mb-1.5">
                Select a lane — {activeLane.n} / {activeLane.title}
              </p>
              <p className="text-sm text-white/70 font-light leading-relaxed">{activeLane.desc}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* LANE STAGE (desktop) */}
      <div className="relative flex-1 hidden lg:block z-10" onMouseLeave={() => !going && setHover(null)}>
        {/* ambient light source — sits at the far horizon where the track lines converge */}
        <div className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(ellipse 30% 13% at ${VP.x}% 31%, rgba(255,255,255,0.11) 0%, rgba(255,180,120,0.05) 45%, transparent 68%)` }} />

        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
          <defs>
            <linearGradient id="laneFade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0" />
              <stop offset="30%" stopColor="#fff" stopOpacity="0" />
              <stop offset="48%" stopColor="#fff" stopOpacity="1" />
              <stop offset="100%" stopColor="#fff" stopOpacity="1" />
            </linearGradient>
            <mask id="fadeMask"><rect x="0" y="0" width="100" height="100" fill="url(#laneFade)" /></mask>
            <linearGradient id="beam" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.18" />
              <stop offset="45%" stopColor="#fff" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="trackSurface" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#c2410c" stopOpacity="0" />
              <stop offset="55%" stopColor="#c2410c" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#c2410c" stopOpacity="0.14" />
            </linearGradient>
            <linearGradient id="trackActive" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff5a1f" stopOpacity="0" />
              <stop offset="45%" stopColor="#ff8a4a" stopOpacity="0.13" />
              <stop offset="100%" stopColor="#ffd9c2" stopOpacity="0.30" />
            </linearGradient>
          </defs>

          {/* Faint spotlight cone kept in-stage for depth (source bloom only) */}

          {/* Track surface — warm lane channels between the painted white lines */}
          <g mask="url(#fadeMask)">
            {LANES.map((lane, i) => {
              const laneActive = !going && i === active;
              return (
                <polygon key={lane.key}
                  points={`${VP.x},${VP.y} ${BX[i]},112 ${BX[i + 1]},112`}
                  fill={laneActive ? "url(#trackActive)" : "url(#trackSurface)"} />
              );
            })}
          </g>

          {/* Lane boundary lines — one soft solid line each; hovered lane illuminates LED-style */}
          <g mask="url(#fadeMask)">
            {BX.map((x, k) => {
              const isActive = !going && (k === active || k === active + 1);
              return (
                <g key={k}>
                  {isActive && (
                    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, ease: "easeOut" }}>
                      <line x1={VP.x} y1={VP.y} x2={x} y2={112} stroke="rgba(255,255,255,0.35)" strokeWidth="22"
                        vectorEffect="non-scaling-stroke" style={{ filter: "blur(16px)" }} />
                      <line x1={VP.x} y1={VP.y} x2={x} y2={112} stroke="rgba(255,255,255,0.85)" strokeWidth="9"
                        vectorEffect="non-scaling-stroke" style={{ filter: "blur(6px)" }} />
                      <line x1={VP.x} y1={VP.y} x2={x} y2={112} stroke="rgba(255,255,255,1)" strokeWidth="4"
                        vectorEffect="non-scaling-stroke" style={{ filter: "blur(1.2px)" }} />
                    </motion.g>
                  )}
                  <motion.line x1={VP.x} y1={VP.y} x2={x} y2={112}
                    stroke={isActive ? "#ffffff" : "rgba(255,255,255,0.85)"}
                    strokeWidth={isActive ? 2.4 : 1.4}
                    vectorEffect="non-scaling-stroke"
                    style={{ filter: isActive ? "drop-shadow(0 0 2px rgba(255,255,255,0.95))" : "none" }}
                    initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ delay: 0.55 + k * 0.09, duration: 1.2, ease: [0.16, 1, 0.3, 1] }} />
                </g>
              );
            })}
          </g>
        </svg>

        {/* Full-lane hover hit areas (invisible) — hovering anywhere in a lane highlights it */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-[2]">
          {LANES.map((lane, i) => (
            <polygon key={lane.key}
              points={`${VP.x},${VP.y} ${BX[i]},112 ${BX[i + 1]},112`}
              fill="transparent" style={{ pointerEvents: "all", cursor: "pointer" }}
              onMouseEnter={() => { if (!going) { setHover(i); preloadLane(lane.key); } }}
              onClick={() => !going && go(lane)} />
          ))}
        </svg>

        <p className="absolute left-6 md:left-10 top-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/35 z-20 sr-only">Select a lane</p>

        {/* Labels */}
        {lanes.map((lane, i) => {
          const isActive = active === i;
          const geo = laneGeo[i];
          const align = i === 2 ? "items-center text-center" : i > 2 ? "items-end text-right" : "items-start text-left";
          return (
            <div key={lane.key} data-testid={`lane-${lane.key}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col ${align} cursor-pointer z-10`}
              style={{ left: `${geo.lx}%`, top: `${geo.ly}%`, width: "min(210px, 15vw)" }}
              onMouseEnter={() => { if (!going) { setHover(i); preloadLane(lane.key); } }}
              onClick={() => !going && go(lane)}>
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                animate={going === lane.key ? { x: `${VP.x - geo.lx}%`, y: `${VP.y - geo.ly}%`, scale: 0.4, opacity: 0 } : { opacity: 1, y: 0 }}
                transition={going === lane.key ? { duration: 0.5, ease: [0.65, 0, 0.35, 1] } : { delay: 0.7 + i * 0.1, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                className={`flex flex-col w-full ${align}`}>
                <motion.h3
                  animate={{ color: isActive ? "#ffffff" : "#8b8b92" }}
                  transition={{ duration: 0.4 }}
                  className={`font-display font-extrabold italic uppercase tracking-tight leading-[0.9] ${isActive ? "text-2xl md:text-[1.9rem]" : "text-lg md:text-xl"}`}
                  style={isActive ? { textShadow: "0 2px 24px rgba(0,0,0,0.7)" } : undefined}>
                  {lane.title}
                </motion.h3>
                <motion.span animate={{ color: isActive ? "#d4d4d8" : "#6b6b72" }}
                  className="mt-2 font-display font-semibold text-lg tracking-[0.15em]">{lane.n}</motion.span>
                <AnimatePresence>
                  {isActive && !going && (
                    <motion.button data-testid={`lane-go-${lane.key}`}
                      onClick={(e) => { e.stopPropagation(); go(lane); }}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 flex items-center justify-center w-24 h-8 rounded-md border border-white/70 bg-transparent text-white font-mono text-[11px] tracking-[0.35em] uppercase hover:bg-white hover:text-black hover:border-black active:bg-white active:text-black active:border-black transition-colors duration-300">
                      <span className="ml-[0.35em]">GO</span>
                    </motion.button>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          );
        })}
      </div>

      {/* MOBILE lane list */}
      <div className="lg:hidden flex-1 overflow-y-auto px-6 py-6 space-y-1 relative z-20">
        {lanes.map((lane) => (
          <button key={lane.key} data-testid={`lane-${lane.key}-mobile`} onClick={() => navigate(lane.route)}
            className="w-full text-left border-t border-white/10 py-4 flex items-center justify-between">
            <div>
              <span className="font-mono text-[11px] text-white/40 mr-3">{lane.n}</span>
              <span className="font-mono text-sm tracking-[0.1em] uppercase">{lane.title}</span>
            </div>
            <ArrowRight size={16} className="text-white/40" />
          </button>
        ))}
      </div>

      {/* GALLERY FILMSTRIP — always visible and scrolling */}
      <motion.div data-testid="home-gallery-filmstrip"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-20 shrink-0 overflow-hidden border-t border-white/10 bg-black/40">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {imgs.concat(imgs).map((p, i) => (
            <button key={i} data-testid={`carousel-photo-${i}`} onClick={() => setLightbox(p)}
              className="block h-24 md:h-32 w-40 md:w-56 mx-0.5 overflow-hidden bg-neutral-900 cursor-zoom-in">
              <img src={p.thumb || p.src} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </Marquee>
      </motion.div>

      {/* LIGHTBOX — click a carousel photo to enlarge */}
      <AnimatePresence>
        {lightbox && (
          <motion.div data-testid="lightbox-overlay" onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[95] bg-black/90 flex items-center justify-center p-6 md:p-12 cursor-zoom-out"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}>
            <button data-testid="lightbox-close" onClick={() => setLightbox(null)}
              className="absolute top-5 right-6 z-10 w-11 h-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors">
              <X size={18} />
            </button>
            <motion.figure
              className="flex flex-col items-center max-w-full max-h-full cursor-zoom-out"
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
              <img data-testid="lightbox-image" src={lightbox.src} alt={lightbox.title}
                className="max-w-full max-h-[80vh] object-contain shadow-2xl" />
              {lightbox.title && (
                <figcaption data-testid="lightbox-caption"
                  className="mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white/70 text-center">
                  {lightbox.title}
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STATS FOOTER */}
      <div className="relative z-20 shrink-0 border-t border-white/10 py-3 px-6 md:px-10 overflow-hidden">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
          {(content.stats || []).map((s, i) => (
            <span key={i} className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-white/35">
              {s}{i < (content.stats || []).length - 1 && <span className="ml-6 text-white/15">·</span>}
            </span>
          ))}
        </div>
      </div>
      </motion.div>

      {/* WHITE PORTAL — the destination emerges from the light. Uses a fixed-size
          circle scaled via transform (GPU only) instead of animating width/height. */}
      <AnimatePresence>
        {going && (
          <motion.div className="fixed z-[70] rounded-full bg-white pointer-events-none"
            style={{
              left: "50%",
              top: "40%",
              width: "12vmax",
              height: "12vmax",
              marginLeft: "-6vmax",
              marginTop: "-6vmax",
              transformOrigin: "50% 50%",
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 32, opacity: 1 }}
            transition={{ duration: reduce ? 0.22 : 0.55, ease: [0.65, 0, 0.35, 1] }} />
        )}
      </AnimatePresence>
    </div>
  );
}
